import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import { LoginCorporationData, LoginResponse } from "@/api/dto/Login";
import { ReviewForm } from "@/views/review/ReviewForm";
import { CorporateReviewForm } from "@/views/corporate/review/ReviewForm";
import { DarkMode, UserProfile } from "@/api/dto/UserProfile";
import { Language, Tool } from "@/api/dto/Skill";
import { login, loginRegisterUsers } from "@/api/Login";
import { getUserProfile } from "@/api/UserProfile";
import { hasAccessToken, removeTokenPair, updateAuthCodeFlag, updateTokenPair } from "./jwt";
import { CorpInfo } from "./api/dto/CorpInfo";
import { getCorpInfo } from '@/api/CorpInfo';

Vue.use(Vuex);

/**
 * 空のユーザープロフィール
 */
export const emptyUserProfile = (): UserProfile => {
  return {
    id: "",
    userId: "",
    mailAddress: "",
    iconUrl: "",
    nickname: "",
    skill: {
      language: [],
      tool: [],
    },
    codeTheme: DarkMode,
    isTwoFactor: true,
    isFreeTime: false,
    registeredCreditCard: false,
    accountStatus: "",
    userCategory: "",
    corporateUserCategory: "",
    corporationsData: {} as LoginCorporationData,
  };
};

/**
 * 空のレビューフォーム
 */
export const emptyReviewForm = (): ReviewForm => {
  return {
    dealId: null,
    dealStatus: "",
    language: "",
    title: "",
    purchaseOverview: "",
    codeEditor: [],
    purchaseType: "1",
    amount: 3000,
    limitCount: 0,
    publishedPeriod: "",
    selectPublishedPeriod: 2,
    autoTenderType: "2",
    tenderPriority: "0",
    tags: "",
    orderSkills: null,
    createDate: "",
    viewPermission: "1",
    isUseCoupon: false,
  };
};

/**
 * 空の法人レビューフォーム
 */
export const emptyCorporateReviewForm = (): CorporateReviewForm => {
  return {
    dealId: null,
    dealStatus: "",
    title: "",
    purchaseOverview: "",
    codeEditor: [],
    amount: 3000,
    tags: "",
    createDate: "",
    dealType: "1",
    githubUrl: "",
    fileUrl: "",
    language: "",
    projectName: "",
    projectIconUrl: "",
    projectId: "",
  };
};

/**
 * 空の法人情報フォーム
 */
export const emptyCorpInfo = (): CorpInfo => {
  return {
    corporateId: "",
    managementUserId: "",
    corporateName: "",
    corporateNameKana: "",
    divisionName: "",
    corporateRepresentative: "",
    corporateRepresentativeKana: "",
    phoneNumber: "",
    fax: "",
  };
};

/**
 *
 * ログイン情報からユーザープロフィールを作成する
 */
const toUserProfile = (loginResponse: LoginResponse): UserProfile => {
  return {
    id: loginResponse.id,
    userId: loginResponse.userId,
    mailAddress: loginResponse.mailAddress,
    iconUrl: loginResponse.iconUrl,
    nickname: loginResponse.nickname,
    skill: {
      language: loginResponse.skills.filter((skill) => skill.type === Language),
      tool: loginResponse.skills.filter((skill) => skill.type === Tool),
    },
    codeTheme: loginResponse.codeTheme,
    isTwoFactor: loginResponse.isTwoFactor,
    isFreeTime: loginResponse.isFreeTime,
    registeredCreditCard: loginResponse.registeredCreditCard,
    accountStatus: loginResponse.accountStatus,
    userCategory: loginResponse.userCategory,
    corporateUserCategory: loginResponse.corporateUserCategory,
    corporationsData: loginResponse.corporationsData,
  };
};

export interface State {
  /**
   * アクセストークンの有無と同期する
   */
  hasAuth: boolean,
  isValidatedAuthCode: boolean,
  mode: string;
  /** ユーザーデータ */
  user: UserProfile;
  /** レビューフォームデータ */
  review: ReviewForm;
  /** レビューフォームデータ（法人） */
  corporateReview: CorporateReviewForm;
  /** レビューフォームファイルデータ（法人） */
  corporateReviewFile: File | null;
  /** 選択中の入札者情報 */
  selectedTenderUser: {
    /** 入札 ID */
    tenderUserId?: string;
    /** 受注希望金額 */
    amount?: number;
  };
  /** 法人情報フォームデータ */
  corpInfo: CorpInfo;
}

const initState = (): State => {
  return {
    hasAuth: false,
    isValidatedAuthCode: false,
    mode: "",
    user: emptyUserProfile(),
    review: emptyReviewForm(),
    corporateReview: emptyCorporateReviewForm(),
    corporateReviewFile: null,
    selectedTenderUser: {
      tenderUserId: "",
      amount: 0,
    },
    corpInfo: emptyCorpInfo(),
  };
};

export default new Vuex.Store<State>({
  state: initState(),
  getters: {
    /**
     * 認証情報が存在するか
     */
    hasAuth: (state: State) => state.hasAuth,
    /**
     * 番号認証は済んでいるのか
     */
    isValidatedAuthCode: (state: State) => state.isValidatedAuthCode,
    /** ユーザーデータ */
    userProfile: (state: State) => state.user,
    /** レビューフォームデータ */
    reviewForm: (state: State) => state.review,
    /** レビューフォームデータ(法人) */
    corporateReviewForm: (state: State) => state.corporateReview,
    /** レビューフォームファイルデータ（法人） */
    corporateReviewFile: (state: State) => state.corporateReviewFile,
    /** 選択中の入札者情報 */
    selectedTenderUser: (state: State) => state.selectedTenderUser,
    /** 法人情報 */
    corpInfo: (state: State) => state.corpInfo,
  },
  mutations: {
    setHasAuth(state, { hasAuth }: { hasAuth: boolean }) {
      state.hasAuth = hasAuth;
    },
    setMode(state, { mode }: { mode: string }) {
      state.mode = mode;
    },
    setIsValidatedAuthCode(state, { isValidatedAuthCode }: { isValidatedAuthCode: boolean }) {
      state.isValidatedAuthCode = isValidatedAuthCode;
    },
    setUser(state, { user }: { user: Partial<UserProfile> }) {
      state.user = {
        ...state.user,
        ...user,
      };
    },
    setReview(state, { review }: { review: ReviewForm }) {
      state.review = review;
    },
    setCorporateReview(state, { corporateReview }: { corporateReview: CorporateReviewForm }) {
      state.corporateReview = corporateReview;
    },
    setCorporateReviewFile(state, zipFile: File) {
      if (zipFile instanceof File || zipFile === null) {
        state.corporateReviewFile = zipFile;
      } else {
        console.error("Invalid file type:", zipFile);
      }
    },
    /**
     * 選択した入札者の情報をセット
     * NOTE: 支払情報入力画面へ渡す入札者の情報を格納する。
     *       入札一覧画面・入札用支払情報入力画面での利用を想定
     */
    setSelectedTenderUser(state, { tenderUserId, amount }: { tenderUserId: string; amount: number }) {
      state.selectedTenderUser = {
        tenderUserId,
        amount,
      };
    },
    setCorpInfo(state, { corpInfo }: { corpInfo: CorpInfo }) {
      state.corpInfo = corpInfo;
    },
  },
  actions: {
    async updateAuth({ commit, state }) {
      // アクセストークンが設定されている場合, ユーザーデータ取得を行う
      // NOTE: 新規タブを開いたとき, アクセストークンは共有されている
      const hasAuth = hasAccessToken();
      if (!state.hasAuth && hasAuth) {
        await this.dispatch("userUpdate");
      }
      commit('setHasAuth', { hasAuth });
    },
    /**
     * ログインを行う
     *
     * - ログインAPIによりユーザー情報とアクセストークン, 有効期限とリフレッシュトークンを取得
     * - それらをlocalStorageに保存
     */
    async login({ commit }, { corporateId, mailAddress, password }: { corporateId: string; mailAddress: string; password: string }) {
      const res = await login({ corporateId, mailAddress, password });
      updateTokenPair(res.accessToken, res.accessTokenExpiresAt, res.refreshToken);
      updateAuthCodeFlag("false");
      commit("setHasAuth", { hasAuth: true });
      commit("setIsValidatedAuthCode", { isValidatedAuthCode: false });
      commit("setUser", { user: toUserProfile(res) });
    },

    /**
     * 新規会員登録完了後にログインを行う
     *
     */
    async loginRegisterUsers({ commit }, { id }: { id: string }) {
      const res = await loginRegisterUsers(id);
      updateTokenPair(res.accessToken, res.accessTokenExpiresAt, res.refreshToken);
      updateAuthCodeFlag("true");
      commit("setHasAuth", { hasAuth: true });
      commit("setIsValidatedAuthCode", { isValidatedAuthCode: false });
      commit("setUser", { user: toUserProfile(res) });
    },

    /**
     * ログアウト処理
     *
     * - アクセストークン, 有効期限, ユーザーデータを消去する
     */
    async logout({ commit }) {
      removeTokenPair();
      commit("setHasAuth", { hasAuth: false });
      commit("setIsValidatedAuthCode", { isValidatedAuthCode: false });
      commit("setUser", { user: emptyUserProfile() });
    },
    /**
     * ユーザープロフィールを取得し, userを更新
     */
    async userUpdate({ commit }) {
      const res = await getUserProfile();

      if (!res.userId) {
        return;
      }

      const newUser: Partial<UserProfile> = {
        id: res.id,
        userId: res.userId,
        iconUrl: res.iconUrl,
        nickname: res.nickname,
        skill: {
          language: res.skills.filter((skill) => skill.type === Language),
          tool: res.skills.filter((skill) => skill.type === Tool),
        },
        codeTheme: res.codeTheme,
        registeredCreditCard: res.registeredCreditCard,
        userCategory: res.userCategory,
        corporateUserCategory: res.corporateUserCategory,
        corporationsData: {
          corporateId: res.corporationsData?.corporateId,
          corporateName: res.corporationsData?.corporateName,
          isManagementUser: res.corporationsData?.isManagementUser,
          paymentType: res.corporationsData?.paymentType,
          registeredCreditCard: res.corporationsData?.registeredCreditCard,
          iconUrl: res.corporationsData?.iconUrl,
        }
      };
      commit("setUser", { user: newUser });
    },
    // レビューフォームデータ初期化
    initReviewForm({ commit }) {
      commit("setReview", { review: emptyReviewForm() });
    },
    // レビューフォームデータ初期化（法人）
    initCorporateReviewForm({ commit }) {
      commit("setCorporateReview", { corporateReview: emptyCorporateReviewForm() });
    },
    // レビューフォームファイルデータ初期化（法人）
    initCorporateReviewFile({ commit }) {
      commit("setCorporateReviewFile", null );
    },
    // レビューフォームデータ更新
    updateReviewForm({ commit }, { form }: { form: ReviewForm }) {
      commit("setReview", { review: form });
    },
    // レビューフォームデータ更新（法人）
    updateCorporateReviewForm({ commit }, { form }: { form: CorporateReviewForm }) {
      commit("setCorporateReview", { corporateReview: form });
    },
    // レビューフォームファイルデータ更新（法人）
    uploadCorporateReviewFile({ commit }, file: File) {
      commit("setCorporateReviewFile", file );
    },
    /** 選択中の入札者情報をリセット */
    initTenderUser({ commit }) {
      commit("setSelectedTenderUser", {
        tenderUserId: undefined,
        amount: undefined,
      });
    },
    /** 入札者を選択 */
    selectTenderUser({ commit }, { tenderUserId, amount }: { tenderUserId: string; amount: number }) {
      commit("setSelectedTenderUser", { tenderUserId, amount });
    },
    /** sessionストレージ内のaccount_statusとicon_urlを更新 */
    async updateAccountStatusAndIconUrlInSession() {
      const res = await getUserProfile();

      if (!res.userId) {
        return;
      }

      let sessionUser = this.getters.userProfile
      if (sessionUser.iconUrl != res.iconUrl) {
        this.getters.userProfile.iconUrl = res.iconUrl;
      }
      if (sessionUser.accountStatus != res.accountStatus) {
        this.getters.userProfile.accountStatus = res.accountStatus;
      }
    },
    /** sessionストレージ内のaccount_statusを更新 */
    async updateAccountStatus() {
      const res = await getUserProfile();

      if (!res.userId) {
        return;
      }

      let sessionUser = this.getters.userProfile
      if (sessionUser.accountStatus != res.accountStatus) {
        this.getters.userProfile.accountStatus = res.accountStatus;
      }
    },
    /** ローカルストレージの番号認証済フラグをsessionストレージのものと同期する */
    updateValidateFlag({ commit }, { isValidatedAuthCode }: { isValidatedAuthCode: boolean }) {
      commit("setIsValidatedAuthCode", { isValidatedAuthCode });
    },
    /** 番号認証が済んでいるかを判定し、済んでいなければログアウトする */
    checkValidated() {
      if (!this.getters.isValidatedAuthCode) {
        this.dispatch("logout");
      }
    },
    /**
     * 法人情報を取得し, corpInfoを更新
     */
    async corpInfoUpdate({ commit }) {
      const res = await getCorpInfo();
      const newCorpInfo: Partial<CorpInfo> = {
        corporateId: res.corporateId,
        managementUserId: res.managementUserId,
        corporateName: res.corporateName,
        corporateNameKana: res.corporateNameKana,
        divisionName: res.divisionName,
        corporateRepresentative: res.corporateRepresentative,
        corporateRepresentativeKana: res.corporateRepresentativeKana,
        phoneNumber: res.phoneNumber,
        fax: res.fax,
      };
      commit("setCorpInfo", { corpInfo: newCorpInfo });
    },
    // 法人情報フォームデータ初期化
    initCorpInfoForm({ commit }) {
      commit("setCorpInfo", { corpInfo: emptyCorpInfo() });
    },
    // 法人情報フォームデータ更新
    updateCorpInfoForm({ commit }, { form }: { form: CorpInfo }) {
      commit("setCorpInfo", { corpInfo: form });
    },
  },
  plugins: [
    createPersistedState({
      key: "idhReviewMarkets",
      storage: window.sessionStorage,
    }),
  ],
});
